<template>
  <div class="tournament-embed-register">
    <!-- Language Selector in top right -->
    <div class="language-selector-container">
      <LanguageSelector />
    </div>    <!-- Main Registration Content -->
    <div class="registration-container">
      <!-- Form View -->
      <v-card v-if="!formSubmitted && !errorOccurred" class="registration-card" elevation="3">
        <!-- Tournament Header -->
        <v-card-text class="tournament-header pb-0">
          <div class="tournament-icon">
            <i class="fa-solid fa-trophy"></i>
          </div>
          <h1 class="tournament-title">{{ tournamentName }}</h1>
          <p class="tournament-organizer">
            {{ $t('organizedBy') }} <span class="organizer-highlight">{{ tournamentOrganizer }}</span>
          </p>
          
          <!-- Tournament Status -->
          <div class="status-container">
            <span :class="['status-chip', tournamentStatus.toLowerCase().replace(' ', '-')]">
              <i class="fa-solid fa-circle status-indicator"></i>
              {{ tournamentStatus }}
            </span>
          </div>
        </v-card-text>

        <!-- Divider -->
        <v-divider class="mx-6 my-4"></v-divider>

        <!-- Registration Form Section -->
        <v-card-text class="registration-form-section pt-0">
          <!-- Call to Action -->
          <div class="cta-section">
            <h2 class="cta-title">{{ $t('joinTournamentTitle') }}</h2>
            <p class="cta-description">{{ $t('joinTournamentDescription') }}</p>
          </div>

          <!-- Registration Form -->
          <v-form ref="registrationForm" v-model="formValid" class="registration-form">
            <v-row>
              <!-- Name Fields -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formData.firstName"
                  :label="$t('firstName')"
                  :rules="[rules.required]"
                  outlined
                  dense
                  class="form-field"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formData.lastName"
                  :label="$t('lastName')"
                  :rules="[rules.required]"
                  outlined
                  dense
                  class="form-field"
                ></v-text-field>
              </v-col>              <!-- Email -->
              <v-col cols="12">
                <v-text-field
                  v-model="formData.email"
                  :label="$t('email_PublicTournamentRegister')"
                  :rules="[rules.required, rules.email]"
                  type="email"
                  outlined
                  dense
                  class="form-field"
                ></v-text-field>
              </v-col><!-- Phone with Flag Selector -->
              <v-col cols="12">
                <v-row no-gutters>
                  <v-col cols="4" sm="3">
                    <v-select
                      v-model="formData.phoneCode"
                      :items="phoneCodeItems"
                      item-text="dialCode"
                      item-value="dialCode"
                      :label="$t('code')"
                      outlined
                      dense
                      class="phone-code-field"
                      @change="updatePhoneCode"
                    >
                      <template v-slot:selection="{ item }">
                        <span class="flag-display">
                          {{ item.flag }} {{ item.dialCode }}
                        </span>
                      </template>
                      <template v-slot:item="{ item }">
                        <span class="flag-item">
                          {{ item.flag }} {{ item.name }} ({{ item.dialCode }})
                        </span>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="8" sm="9">
                    <v-text-field
                      v-model="formData.phoneNumber"
                      :label="$t('phoneNumber')"
                      :rules="[rules.required, rules.phone]"
                      outlined
                      dense
                      class="phone-number-field"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Country -->
              <v-col cols="12">
                <v-select
                  v-model="formData.country"
                  :items="countryItems"
                  item-text="name"
                  item-value="name"
                  :label="$t('country')"
                  :rules="[rules.required]"
                  outlined
                  dense
                  class="form-field"
                >
                  <template v-slot:selection="{ item }">
                    <span class="flag-display">
                      {{ item.flag }} {{ item.name }}
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span class="flag-item">
                      {{ item.flag }} {{ item.name }}
                    </span>
                  </template>
                </v-select>
              </v-col>

              <!-- Server list (SFB-1012 cross-server tournaments) -->
              <v-col v-if="isCrossServer" cols="12">
                <v-select
                  v-model="formData.tournamentScopeKey"
                  :items="scopeItems"
                  :label="$t('serverList_PublicTournamentRegister')"
                  :rules="[v => !!v || $t('serverRequired_PublicTournamentRegister')]"
                  outlined
                  dense
                  class="form-field"
                ></v-select>
              </v-col>

              <!-- Account Login (CXM & Hantec LIVE + cross-server LIVE) -->
              <v-col v-if="showAccountLoginField || crossServerRequiresLogin" cols="12">
                <v-text-field
                  v-model="formData.accountLogin"
                  :label="$t('accountLogin')"
                  :rules="accountLoginRules"
                  type="number"
                  outlined
                  dense
                  class="form-field"
                ></v-text-field>
              </v-col>

              <!-- Exness credentials (Exness API tournaments only) -->
              <template v-if="isExnessTournament">
                <v-col cols="12">
                  <v-text-field
                    v-model="formData.exnessAccountId"
                    :label="$t('exnessAccountId_PublicTournamentRegister')"
                    :rules="exnessFieldRules"
                    type="number"
                    outlined
                    dense
                    class="form-field"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="formData.exnessApiKey"
                    :label="$t('exnessApiKey_PublicTournamentRegister')"
                    :rules="exnessFieldRules"
                    outlined
                    dense
                    class="form-field"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="formData.exnessSecretKey"
                    :label="$t('exnessSecretKey_PublicTournamentRegister')"
                    :rules="exnessFieldRules"
                    type="password"
                    autocomplete="off"
                    outlined
                    dense
                    class="form-field"
                  ></v-text-field>
                  <div class="exness-keys-hint">
                    <a :href="exnessKeysUrl" target="_blank" rel="noopener noreferrer">{{ $t('exnessKeysHint_PublicTournamentRegister') }}</a>
                  </div>
                </v-col>
              </template>

              <!-- Terms and Conditions -->
              <v-col cols="12">
                <v-checkbox
                  v-model="formData.acceptTerms"
                  :rules="[rules.acceptTerms]"
                  class="terms-checkbox"
                >
                  <template v-slot:label>
                    <span class="terms-text">
                      {{ $t('acceptTermsStart') }}
                      <a href="#" @click.prevent="openTerms" class="terms-link">
                        {{ $t('termsAndConditions') }}
                      </a>
                      {{ $t('acceptTermsEnd') }}
                    </span>
                  </template>
                </v-checkbox>
              </v-col>

              <!-- Submit Button -->
              <v-col cols="12">
                <v-btn
                  :disabled="!formValid || submitting"
                  :loading="submitting"
                  color="mainGreen"
                  class="submit-btn"
                  block
                  large
                  @click="submitRegistration"
                >
                <i class="fa-solid fa-user-plus mr-2"></i>
                {{ submitting ? $t('registering') : $t('joinTournament') }}
              </v-btn>            </v-col>
          </v-row>
        </v-form>
        </v-card-text>
      </v-card>      <!-- Success View -->
      <v-card v-else-if="formSubmitted" class="success-section" elevation="3">
        <div class="success-animation">
          <i class="fa-solid fa-check-circle success-icon"></i>
        </div>
        
        <h2 class="success-title">{{ $t('registrationSuccessful') }}</h2>
        <p class="success-description">{{ $t('registrationSuccessDescription') }}</p>

        <!-- User Info Summary -->
        <div class="user-info-card">
          <h3 class="info-title">{{ $t('yourInformation') }}</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">{{ $t('name') }}:</span>
              <span class="info-value">{{ formData.firstName }} {{ formData.lastName }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('email_PublicTournamentRegister') }}:</span>
              <span class="info-value">{{ formData.email }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('phone') }}:</span>
              <span class="info-value">{{ fullPhoneNumber }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('country') }}:</span>
              <span class="info-value">{{ formData.country }}</span>
            </div>
          </div>
        </div>

        <!-- Email Instructions -->
        <div class="email-instructions">
          <div class="instructions-card">
            <i class="fa-solid fa-envelope instructions-icon"></i>
            <h4 class="instructions-title">{{ $t('checkYourEmail') }}</h4>
            <p class="instructions-text">{{ $t('emailInstructionsDescription') }}</p>
          </div>
        </div>        <!-- Action Buttons -->
        <div class="success-actions">
          <v-btn
            color="mainGreen"
            outlined
            class="action-btn"
            @click="viewTournament"
          >
            <i class="fa-solid fa-trophy mr-2"></i>
            {{ $t('viewTournament') }}
          </v-btn>        </div>
      </v-card>

      <!-- Error View -->
      <v-card v-else-if="errorOccurred" class="error-section" elevation="3">
        <div class="error-animation">
          <i class="fa-solid fa-exclamation-triangle error-icon"></i>
        </div>
        
        <h2 class="error-title">{{ $t('registrationFailed') }}</h2>
        <p class="error-description">{{ errorMessage }}</p>

        <!-- Action Buttons -->
        <div class="error-actions">
          <v-btn
            color="mainGreen"
            class="action-btn"
            @click="tryAgain"
          >
            <i class="fa-solid fa-redo mr-2"></i>
            {{ $t('commonIssue').split('\n')[1] }}
          </v-btn>
        </div>
      </v-card>
    </div>    <!-- Footer -->
    <!--<div class="embed-footer">
      <div class="powered-by">
        {{ $t('poweredBy') }} <a href="https://swiset.com" target="_blank" class="swiset-link">Swiset</a>
      </div>
    </div>-->
  </div>
</template>

<script>
import CountriesData from "@/views/Register/countries";
import PhoneCodes from "@/views/Register/phoneCodes";
import TournamentRepository from "@/repositories/tournament";
import LanguageSelector from "@/components/LanguageSelector.vue";
import { setLocale, i18nEventBus } from "@/i18n";
import { getRecaptchaToken } from '@/services/recaptcha';

export default {
  name: 'TournamentEmbedRegister',
  components: {
    LanguageSelector
  },  data() {
    return {
      tournamentData: null,
      formValid: false,      
      formSubmitted: false,
      errorOccurred: false,
      submitting: false,
      registeredAccountLogin: '',
      errorMessage: '',
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        phoneCode: '+1',
        phoneNumber: '',
        country: '',
        accountLogin: '',
        tournamentScopeKey: null,
        exnessAccountId: '',
        exnessApiKey: '',
        exnessSecretKey: '',
        acceptTerms: false
      },
      exnessKeysUrl: 'https://my.exness.com/pa/settings/api-keys',
      // SFB-1012: platforms/servers the tournament is open to
      tournamentScopes: [],
      rules: {
        required: v => !!v || this.$t('fieldRequired'),
        email: v => /.+@.+\..+/.test(v) || this.$t('emailInvalid'),
        phone: v => /^\d{7,15}$/.test(v) || this.$t('phoneInvalid'),
        accountLogin: v => !v || /^\d+$/.test(v) || this.$t('mustBeNumeric'),
        accountLoginRequired: v => !!v || this.$t('fieldRequired'),
        acceptTerms: v => !!v || this.$t('mustAcceptTerms')
      },
      refreshKey: 0
    };
  },  computed: {
    tournamentId() {
      // Return the full tournament ID for API calls
      return this.$route.query.tournamentId;
    },
    externalTournamentId() {
      // Extract the external ID part before the dash for API calls
      const rawTournamentId = this.$route.query.tournamentId;
      if (!rawTournamentId) return null;
      
      const dashIndex = rawTournamentId.indexOf('-');
      if (dashIndex !== -1) {
        return rawTournamentId.substring(0, dashIndex);
      }
      
      // If no dash found, return the original value (backward compatibility)
      return rawTournamentId;
    },
    numericTournamentId() {
      // Extract only the numeric part after the dash for registration payload
      const rawTournamentId = this.$route.query.tournamentId;
      if (!rawTournamentId) return null;
      
      const dashIndex = rawTournamentId.indexOf('-');
      if (dashIndex !== -1 && dashIndex < rawTournamentId.length - 1) {
        return rawTournamentId.substring(dashIndex + 1);
      }
      
      // If no dash found, return the original value (backward compatibility)
      return rawTournamentId;
    },countryItems() {
      return CountriesData.map(country => ({
        name: country.name,
        flag: country.flag,
        text: `${country.flag} ${country.name}`,
        value: country.name
      }));
    },
    phoneCodeItems() {
      return CountriesData.map(country => ({
        name: country.name,
        code: country.code,
        flag: country.flag,
        dialCode: PhoneCodes[country.code] || '+1',
        value: PhoneCodes[country.code] || '+1'
      })).sort((a, b) => a.name.localeCompare(b.name));
    },
    fullPhoneNumber() {
      return `${this.formData.phoneCode}${this.formData.phoneNumber}`;
    },
    tournamentName() {
      return this.tournamentData?.tournamentName || 'Tournament';
    },
    tournamentOrganizer() {
      return this.tournamentData?.tournamentOrganizer || 'Swiset';
    },
    tournamentStatus() {
      if (!this.tournamentData) return 'Loading';

      const now = new Date();
      const startTime = new Date(this.tournamentData.startTime);
      const endTime = new Date(this.tournamentData.endTime);

      if (now < startTime) {
        return 'STILL OPEN';
      } else if (now >= startTime && now <= endTime) {
        return 'IN PROGRESS';
      } else {
        return 'CLOSED';
      }
    },
    showAccountLoginField() {
      if (!this.tournamentData) return false;

      // Check for CXM & Hantec partners at root level or in tournamentSettings
      const partnerId = this.tournamentData.mandatoryPartner?.id ||
                       this.tournamentData.tournamentSettings?.mandatoryPartner?.id;

      const accountType = this.tournamentData.tournamentSettings?.restrictedAccountType;

      return (partnerId === 71 || partnerId === 36) && accountType === 'LIVE';
    },
    accountLoginRules() {
      if (this.showAccountLoginField || this.crossServerRequiresLogin) {
        return [this.rules.accountLoginRequired, this.rules.accountLogin];
      }
      return [];
    },
    // SFB-1012: >1 scope = cross-server tournament — the form must ask which
    // platform/server the account lives on. Exactly 1 scope keeps the legacy
    // payload (backend resolves it alone), 0 means legacy flows entirely.
    isCrossServer() {
      return this.tournamentScopes.length > 1;
    },
    crossServerRequiresLogin() {
      return this.isCrossServer
        && this.tournamentData?.tournamentSettings?.restrictedAccountType === 'LIVE';
    },
    scopeItems() {
      return this.tournamentScopes.map(scope => ({
        value: scope.scopeKey,
        text: [scope.platformAlias || scope.platformName, scope.serverName]
          .filter(Boolean).join(' — ') || scope.scopeKey,
      }));
    },
    isExnessTournament() {
      // Tournaments wired to the Exness Public Trader API register through
      // /ext/tournaments/register-exness with user-supplied credentials.
      const platformName = this.tournamentData?.tournamentSettings?.platformServerGroup
        ?.platformServerDetails?.platformId?.name;
      return platformName === 'Exness API';
    },
    exnessFieldRules() {
      if (this.isExnessTournament) {
        return [this.rules.required];
      }
      return [];
    }
  },
  async mounted() {
    // Set language based on tournament organizer
    await this.loadTournamentData();
    this.fetchTournamentScopes();
    this.setLanguageBasedOnTournament();
    
    // Set default country if available
    if (this.$store.state.user?.details?.country) {
      this.formData.country = this.$store.state.user.details.country;
    }

    // Prefill the rest of the form from the active session, if any
    this.prefillFromSession();

    // Listen for language changes
    i18nEventBus.$on('language-changed', () => {
      this.refreshKey += 1;
    });
  },
  beforeDestroy() {
    i18nEventBus.$off('language-changed');
  },  methods: {    async loadTournamentData() {
      if (!this.externalTournamentId) {
        this.showError(this.$t('tournamentNotFound'));
        return;
      }

      try {
        const response = await TournamentRepository.getTournamentOverviewOpen(this.externalTournamentId);
        if (response.status === 200) {
          this.tournamentData = response.data;
        } else {
          this.showError(this.$t('errorLoadingTournament'));
        }
      } catch (error) {
        this.showError(this.$t('errorLoadingTournament'));
      }
    },
    // SFB-1012: which platforms/servers is this tournament open to. Public
    // endpoint; failure silently degrades to the legacy single-server form.
    async fetchTournamentScopes() {
      if (!this.externalTournamentId) return;
      try {
        const response = await TournamentRepository.getTournamentScopes(this.externalTournamentId);
        if (response.status < 400 && Array.isArray(response.data)) {
          this.tournamentScopes = response.data;
          if (this.tournamentScopes.length === 1) {
            this.formData.tournamentScopeKey = this.tournamentScopes[0].scopeKey;
          }
        }
      } catch (_) {
        this.tournamentScopes = [];
      }
    },
    updatePhoneCode() {
      // Phone code selection should not affect country field
      // This method is kept for potential future use but does nothing now
    },
    resolveCountryDialCode(countryName) {
      const country = CountriesData.find(c => c.name === countryName);
      return (country && PhoneCodes[country.code]) || '';
    },
    prefillFromSession() {
      const user = this.$store.state.user;
      if (!this.$store.state.token || !user || user.id === 0) {
        return;
      }

      if (!this.formData.firstName && typeof user.firstName === 'string' && user.firstName) {
        this.formData.firstName = user.firstName;
      }
      if (!this.formData.lastName && typeof user.lastName === 'string' && user.lastName) {
        this.formData.lastName = user.lastName;
      }
      if (!this.formData.email && typeof user.email === 'string' && user.email) {
        this.formData.email = user.email;
      }

      if (!this.formData.phoneNumber && typeof user.details?.phoneNumber === 'string') {
        const storedPhone = user.details.phoneNumber.trim();
        const dialCode = this.resolveCountryDialCode(this.formData.country);
        let national = storedPhone;
        if (storedPhone.startsWith('+')) {
          // Only strip a leading dial code that matches the user's country; otherwise don't guess
          national = dialCode && storedPhone.startsWith(dialCode) ? storedPhone.slice(dialCode.length) : '';
        }
        if (/^\d{7,15}$/.test(national)) {
          this.formData.phoneNumber = national;
          if (storedPhone.startsWith('+')) {
            this.formData.phoneCode = dialCode;
          }
        }
      }
    },
    async submitRegistration() {
      if (!this.$refs.registrationForm.validate()) {
        return;
      }
      this.submitting = true;

      // Get reCAPTCHA token - using same action as register flow for consistency
      const recaptchaToken = await getRecaptchaToken('register');

      const registrationData = {
        tournamentId: parseInt(this.numericTournamentId),
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        email: this.formData.email,
        phoneNumber: this.fullPhoneNumber,
        country: this.formData.country,
        langKey: this.$i18n.locale,
        recaptchaToken: recaptchaToken
      };

      // Add accountLogin for CXM & Hantec LIVE and cross-server LIVE tournaments
      if ((this.showAccountLoginField || this.crossServerRequiresLogin) && this.formData.accountLogin) {
        registrationData.accountLogin = this.formData.accountLogin;
      }

      // SFB-1012: cross-server tournaments route by the selected server's key
      if (this.isCrossServer && this.formData.tournamentScopeKey) {
        registrationData.tournamentScopeKey = this.formData.tournamentScopeKey;
      }

      // Exness API tournaments register through a dedicated endpoint with the
      // participant's own account id + API key/secret (never via process-woocommerce).
      if (this.isExnessTournament) {
        registrationData.exnessAccountId = this.formData.exnessAccountId;
        registrationData.apiKey = this.formData.exnessApiKey;
        registrationData.secretKey = this.formData.exnessSecretKey;
      }

      try {
        const response = this.isExnessTournament
          ? await TournamentRepository.registerExnessTournament(registrationData)
          : await TournamentRepository.registerPublicTournament(registrationData);
        
        if (response.status === 200 || response.status === 201) {
          this.registeredAccountLogin = response.data?.accountLogin || '';
          this.formSubmitted = true;
        } else {
          this.showError(response.data?.message || this.$t('registrationFailed'));
        }
      } catch (error) {
        const code = error?.response?.data?.code;
        // Check if it's a 404 error and accountLogin field was provided
        if (error?.response?.status === 404 && this.showAccountLoginField && this.formData.accountLogin) {
          this.showError(this.$t('accountLoginNotFound'));
        } else if (code === 'TOURNAMENT_REGISTRATION.PLATFORM_ACCOUNT_NOT_FOUND' && this.isExnessTournament) {
          this.showError(this.$t('exnessVerifyFailed_PublicTournamentRegister'));
        } else if (code === 'TOURNAMENT_REGISTRATION.PLATFORM_ACCOUNT_NOT_FOUND') {
          this.showError(this.$t('accountLoginNotFound'));
        } else if (code === 'TOURNAMENT_REGISTRATION.ACCOUNT_LOGIN_DUPLICATE_IN_TOURNAMENT') {
          this.showError(this.$t('accountLoginDuplicate_PublicTournamentRegister'));
        } else if (code === 'TOURNAMENT_REGISTRATION.ACCOUNT_LOGIN_BELONGS_TO_OTHER_USER') {
          this.showError(this.$t('accountLoginOtherUser_PublicTournamentRegister'));
        } else if (code === 'TOURNAMENT_REGISTRATION.PAYLOAD_INCOMPLETE' && this.isCrossServer) {
          this.showError(this.$t('serverRequired_PublicTournamentRegister'));
        } else if (error.response?.data?.message) {
          this.showError(error.response.data.message);
        } else {
          this.showError(this.$t('registrationFailed'));
        }
      } finally {
        this.submitting = false;
      }
    },    showError(message) {
      this.errorMessage = message;
      this.errorOccurred = true;
      this.submitting = false;
    },
    tryAgain() {
      this.errorOccurred = false;
      this.errorMessage = '';
      // Reset to form view
    },
    openTerms() {
      const url = this.tournamentData?.tournamentSettings?.termsCondition;
      const termsUrl = (url && typeof url === 'string' && url.trim() !== '') ? url.trim() : 'https://swiset.com/competitions/terms-and-conditions';
      window.open(termsUrl, '_blank');
    },
    viewTournament() {
      // Navigate to tournament ranking view
      const baseUrl = window.location.origin;
      const tournamentUrl = `${baseUrl}/tournaments/embed-rank?tournamentId=${this.externalTournamentId}&identifier=${this.registeredAccountLogin}`;
      window.open(tournamentUrl, '_blank');
    },
    setLanguageBasedOnTournament() {
      const tournamentId = this.tournamentData?.tournamentSettings?.tournament?.id;
      const targetLocale = this.tournamentData?.tournamentSettings?.mainLanguage;
      if (!tournamentId || !targetLocale) {
        return;
      }

      const languageSetKey = `language_set_for_tournament_${tournamentId}`;

      if (localStorage.getItem(languageSetKey) === 'true') {
        return;
      }

      if (localStorage.getItem(`user_changed_locale_tournament_${tournamentId}`)) {
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      if (this.$i18n.locale === targetLocale) {
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      localStorage.setItem(languageSetKey, 'true');
      setLocale(targetLocale);
      this.$store.commit('setPreferredLanguage', targetLocale);

      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  },
  render(h) {
    return h('div', {
      key: this.refreshKey,
      class: 'tournament-embed-register'
    }, this.$slots.default);
  }
}
</script>

<style lang="scss" scoped>
.tournament-embed-register {
  min-height: 100vh;
  background: #f8f9fa;
  font-family: "Avenir", sans-serif;
  position: relative;

  .language-selector-container {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
  }
  .registration-container {
    max-width: 600px;
    margin: 0 auto;
    padding: 40px 20px 80px;

    // Mobile devices (up to 767px)
    @media (max-width: 767px) {
      padding: 20px 15px 60px;
      max-width: 100%;
    }

    // Tablet devices (768px to 1023px)
    @media (min-width: 768px) and (max-width: 1023px) {
      padding: 35px 25px 70px;
      max-width: 500px;
    }

    // Desktop devices (1024px and up)
    @media (min-width: 1024px) {
      padding: 40px 20px 80px;
      max-width: 600px;
    }
  }
  .tournament-header {
    text-align: center;
    padding: 30px;

    // Mobile devices
    @media (max-width: 767px) {
      padding: 20px 15px;
    }

    // Tablet devices
    @media (min-width: 768px) and (max-width: 1023px) {
      padding: 25px 20px;
    }

    .tournament-icon {
      font-size: 48px;
      color: var(--v-mainGreen-base);
      margin-bottom: 15px;

      // Mobile devices
      @media (max-width: 767px) {
        font-size: 36px;
        margin-bottom: 12px;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        font-size: 42px;
        margin-bottom: 14px;
      }
    }

    .tournament-title {
      font-size: 28px;
      font-weight: 600;
      color: var(--v-mainBlack-base);
      margin-bottom: 8px;
      line-height: 1.3;

      // Mobile devices
      @media (max-width: 767px) {
        font-size: 20px;
        margin-bottom: 6px;
        line-height: 1.2;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        font-size: 24px;
        margin-bottom: 7px;
      }
    }

    .tournament-organizer {
      font-size: 16px;
      color: #666;
      margin-bottom: 20px;

      // Mobile devices
      @media (max-width: 767px) {
        font-size: 14px;
        margin-bottom: 15px;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        font-size: 15px;
        margin-bottom: 18px;
      }

      .organizer-highlight {
        font-weight: 600;
        color: var(--v-mainBlack-base);
      }
    }    .status-container {
      .status-chip {
        display: inline-flex;
        align-items: center;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        // Mobile devices
        @media (max-width: 767px) {
          padding: 6px 12px;
          font-size: 12px;
          letter-spacing: 0.3px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          padding: 7px 14px;
          font-size: 13px;
          letter-spacing: 0.4px;
        }

        .status-indicator {
          font-size: 8px;
          margin-right: 8px;

          // Mobile devices
          @media (max-width: 767px) {
            font-size: 6px;
            margin-right: 6px;
          }

          // Tablet devices
          @media (min-width: 768px) and (max-width: 1023px) {
            font-size: 7px;
            margin-right: 7px;
          }
        }

        &.still-open {
          background-color: #FFF75F;
          color: #000;

          .status-indicator {
            color: #00C853;
          }
        }

        &.in-progress {
          background-color: var(--v-mainGreen-base);
          color: white;

          .status-indicator {
            animation: pulse 2s infinite;
          }
        }

        &.closed {
          background-color: #666;
          color: white;
        }
      }
    }
  }  .registration-form-section {
    padding: 30px;

    // Mobile devices
    @media (max-width: 767px) {
      padding: 20px 15px;
    }

    // Tablet devices
    @media (min-width: 768px) and (max-width: 1023px) {
      padding: 25px 20px;
    }

    .cta-section {
      text-align: center;
      margin-bottom: 30px;

      // Mobile devices
      @media (max-width: 767px) {
        margin-bottom: 20px;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        margin-bottom: 25px;
      }

      .cta-title {
        font-size: 24px;
        font-weight: 600;
        color: var(--v-mainBlack-base);
        margin-bottom: 10px;

        // Mobile devices
        @media (max-width: 767px) {
          font-size: 18px;
          margin-bottom: 8px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          font-size: 21px;
          margin-bottom: 9px;
        }
      }

      .cta-description {
        font-size: 16px;
        color: #666;
        line-height: 1.5;

        // Mobile devices
        @media (max-width: 767px) {
          font-size: 14px;
          line-height: 1.4;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          font-size: 15px;
          line-height: 1.45;
        }
      }
    }    .registration-form {
      .form-field {
        margin-bottom: 8px;

        // Mobile devices
        @media (max-width: 767px) {
          margin-bottom: 6px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          margin-bottom: 7px;
        }
      }

      .phone-code-field {
        border-radius: 8px !important;
        margin-right: 8px;
        margin-bottom: 8px;

        // Mobile devices
        @media (max-width: 767px) {
          margin-right: 6px;
          margin-bottom: 6px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          margin-right: 7px;
          margin-bottom: 7px;
        }
      }

      .phone-number-field {
        border-radius: 4px !important;
        margin-bottom: 8px;

        // Mobile devices
        @media (max-width: 767px) {
          margin-bottom: 6px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          margin-bottom: 7px;
        }
      }

      .exness-keys-hint {
        display: flex;
        justify-content: flex-end;
        margin-top: -6px;

        a {
          font-size: 13px;
          color: var(--v-mainGreen-base);
          text-decoration: underline;
        }
      }

      .terms-checkbox {
        margin-top: 10px;

        // Mobile devices
        @media (max-width: 767px) {
          margin-top: 8px;
        }
        
        .terms-text {
          font-size: 14px;
          line-height: 1.4;

          // Mobile devices
          @media (max-width: 767px) {
            font-size: 12px;
            line-height: 1.3;
          }

          // Tablet devices
          @media (min-width: 768px) and (max-width: 1023px) {
            font-size: 13px;
            line-height: 1.35;
          }
          
          .terms-link {
            color: var(--v-mainGreen-base);
            text-decoration: none;
            font-weight: 500;
            
            &:hover {
              text-decoration: underline;
            }
          }
        }
      }

      .submit-btn {
        margin-top: 20px;
        border-radius: 12px;
        font-weight: 600;
        text-transform: none;
        font-size: 16px;
        padding: 12px 0;

        // Mobile devices
        @media (max-width: 767px) {
          margin-top: 15px;
          font-size: 14px;
          padding: 10px 0;
          border-radius: 8px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          margin-top: 18px;
          font-size: 15px;
          padding: 11px 0;
          border-radius: 10px;
        }
      }
    }
  }  .success-section {
    text-align: center;
    background: white;
    border-radius: 16px;
    padding: 40px 30px;

    // Mobile devices
    @media (max-width: 767px) {
      padding: 25px 20px;
      border-radius: 12px;
    }

    // Tablet devices
    @media (min-width: 768px) and (max-width: 1023px) {
      padding: 35px 25px;
      border-radius: 14px;
    }

    .success-animation {
      margin-bottom: 24px;

      // Mobile devices
      @media (max-width: 767px) {
        margin-bottom: 18px;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        margin-bottom: 21px;
      }

      .success-icon {
        font-size: 64px;
        color: var(--v-mainGreen-base);
        animation: successBounce 0.6s ease-out;

        // Mobile devices
        @media (max-width: 767px) {
          font-size: 48px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          font-size: 56px;
        }
      }
    }

    .success-title {
      font-size: 28px;
      font-weight: 600;
      color: var(--v-mainBlack-base);
      margin-bottom: 12px;

      // Mobile devices
      @media (max-width: 767px) {
        font-size: 20px;
        margin-bottom: 10px;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        font-size: 24px;
        margin-bottom: 11px;
      }
    }

    .success-description {
      font-size: 16px;
      color: #666;
      margin-bottom: 30px;
      line-height: 1.5;

      // Mobile devices
      @media (max-width: 767px) {
        font-size: 14px;
        margin-bottom: 20px;
        line-height: 1.4;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        font-size: 15px;
        margin-bottom: 25px;
        line-height: 1.45;
      }
    }    .user-info-card {
      background: #f8f9fa;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 24px;
      text-align: left;

      // Mobile devices
      @media (max-width: 767px) {
        padding: 15px;
        margin-bottom: 18px;
        border-radius: 8px;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        padding: 18px;
        margin-bottom: 21px;
        border-radius: 10px;
      }

      .info-title {
        font-size: 18px;
        font-weight: 600;
        color: var(--v-mainBlack-base);
        margin-bottom: 16px;
        text-align: center;

        // Mobile devices
        @media (max-width: 767px) {
          font-size: 16px;
          margin-bottom: 12px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          font-size: 17px;
          margin-bottom: 14px;
        }
      }

      .info-grid {
        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e0e0e0;

          // Mobile devices
          @media (max-width: 767px) {
            padding: 6px 0;
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          }

          // Tablet devices
          @media (min-width: 768px) and (max-width: 1023px) {
            padding: 7px 0;
          }

          &:last-child {
            border-bottom: none;
          }

          .info-label {
            font-weight: 500;
            color: #666;

            // Mobile devices
            @media (max-width: 767px) {
              font-size: 12px;
            }

            // Tablet devices
            @media (min-width: 768px) and (max-width: 1023px) {
              font-size: 13px;
            }
          }

          .info-value {
            font-weight: 600;
            color: var(--v-mainBlack-base);

            // Mobile devices
            @media (max-width: 767px) {
              font-size: 13px;
            }

            // Tablet devices
            @media (min-width: 768px) and (max-width: 1023px) {
              font-size: 14px;
            }
          }
        }
      }
    }

    .email-instructions {
      margin-bottom: 30px;

      .instructions-card {
        background: #e8f5e8;
        border-radius: 12px;
        padding: 20px;
        border-left: 4px solid var(--v-mainGreen-base);

        .instructions-icon {
          font-size: 32px;
          color: var(--v-mainGreen-base);
          margin-bottom: 12px;
        }

        .instructions-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--v-mainBlack-base);
          margin-bottom: 8px;
        }

        .instructions-text {
          font-size: 14px;
          color: #666;
          line-height: 1.5;
          margin: 0;
        }
      }
    }

    .success-actions {
      .action-btn {
        border-radius: 12px;
        font-weight: 600;
        text-transform: none;
        padding: 12px 24px;
      }
    }
  }

  .embed-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding: 12px 20px;
    text-align: center;

    .powered-by {
      font-size: 12px;
      color: #666;

      .swiset-link {
        color: var(--v-mainGreen-base);
        text-decoration: none;
        font-weight: 500;

        &:hover {
          text-decoration: underline;
        }
      }
    }  }

  // Error Section Styles
  .error-section {
    text-align: center;
    padding: 40px 30px;
    background: white;
    border-radius: 12px;

    // Mobile devices
    @media (max-width: 767px) {
      padding: 30px 20px;
      border-radius: 8px;
    }

    // Tablet devices
    @media (min-width: 768px) and (max-width: 1023px) {
      padding: 35px 25px;
      border-radius: 10px;
    }

    .error-animation {
      margin-bottom: 24px;

      // Mobile devices
      @media (max-width: 767px) {
        margin-bottom: 18px;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        margin-bottom: 21px;
      }

      .error-icon {
        font-size: 72px;
        color: #f44336;
        animation: errorShake 0.6s ease-in-out;

        // Mobile devices
        @media (max-width: 767px) {
          font-size: 56px;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          font-size: 64px;
        }
      }
    }

    .error-title {
      font-size: 28px;
      font-weight: 700;
      color: #f44336;
      margin-bottom: 16px;

      // Mobile devices
      @media (max-width: 767px) {
        font-size: 22px;
        margin-bottom: 12px;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        font-size: 25px;
        margin-bottom: 14px;
      }
    }

    .error-description {
      font-size: 16px;
      color: #666;
      margin-bottom: 30px;
      line-height: 1.5;

      // Mobile devices
      @media (max-width: 767px) {
        font-size: 14px;
        margin-bottom: 20px;
        line-height: 1.4;
      }

      // Tablet devices
      @media (min-width: 768px) and (max-width: 1023px) {
        font-size: 15px;
        margin-bottom: 25px;
        line-height: 1.45;
      }
    }

    .error-actions {
      .action-btn {
        font-size: 16px;
        font-weight: 600;
        padding: 12px 32px;
        border-radius: 4px;
        text-transform: none;
        height: auto;
        min-height: 48px;

        // Mobile devices
        @media (max-width: 767px) {
          font-size: 14px;
          padding: 10px 24px;
          min-height: 44px;
          width: 100%;
        }

        // Tablet devices
        @media (min-width: 768px) and (max-width: 1023px) {
          font-size: 15px;
          padding: 11px 28px;
          min-height: 46px;
        }

        .v-icon {
          font-size: 18px;

          // Mobile devices
          @media (max-width: 767px) {
            font-size: 16px;
          }
        }
      }
    }
  }
}

@keyframes errorShake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}

@keyframes successBounce {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

// Vuetify overrides
::v-deep .v-text-field {
  .v-input__control {
    .v-input__slot {
      border-radius: 4px !important;
    }
  }
}

::v-deep .v-select {
  .v-input__control {
    .v-input__slot {
      border-radius: 4px !important;
    }
  }
}

::v-deep .v-btn {
  &.v-btn--outlined {
    border-width: 2px !important;
  }
}
</style>
